import { Grid, Paper, Typography, Tooltip, CardContent } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  CloudDownload,
  CloudUploadOutlined,
  InsertDriveFileOutlined,
} from '@material-ui/icons';
import { saveAs } from 'file-saver';
import { DropzoneArea } from 'material-ui-dropzone';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { baseUrl } from '../../../../services/taskAttachmentService';
import { AppState } from '../../../../state/configureStore';
import {
  clearTaskAttachment,
  fetchTaskAttachmentListByTaskId,
  TaskAttachmentState,
  uploadTaskAttachments,
} from '../../../../state/taskAttachment';
import axios from '../../../../utils/axios';

interface TaskAttachmentProps {
  smTaskId: number;
}

const fileSizeInBytes = 50000000;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionCard: {
      width: '100%',
      height: '100%',
      borderRadius: 18,
      border: '1px solid rgba(99, 102, 241, 0.12)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      boxShadow: '0 14px 28px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      padding: theme.spacing(2, 2, 1.25),
    },
    sectionIcon: {
      width: 42,
      height: 42,
      borderRadius: 14,
      display: 'grid',
      placeItems: 'center',
      color: '#2563eb',
      background:
        'linear-gradient(135deg, rgba(37,99,235,0.14) 0%, rgba(99,102,241,0.12) 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.78)',
      flexShrink: 0,
    },
    sectionHeaderText: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.25),
      minWidth: 0,
    },
    sectionTitle: {
      fontWeight: 700,
      color: '#0f172a',
    },
    sectionHint: {
      fontSize: '0.78rem',
      color: '#64748b',
    },
    attachmentContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1.25),
      marginBottom: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 14,
      border: '1px solid rgba(148, 163, 184, 0.14)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      boxShadow: '0 8px 18px rgba(148, 163, 184, 0.08)',
    },
    attachmentTypo: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#0f172a',
      wordBreak: 'break-word',
      lineHeight: 1.35,
    },
    container: {
      margin: theme.spacing(0, 2, 2),
      padding: theme.spacing(1.25),
      borderRadius: 16,
      border: '1px dashed rgba(99, 102, 241, 0.22)',
      background:
        'radial-gradient(circle at top, rgba(99,102,241,0.08), transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.94) 100%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.84)',
    },
    leftIcon: {
      marginRight: '5px',
    },
    title: {
      marginBottom: theme.spacing(1.25),
      fontWeight: 700,
      color: '#0f172a',
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: 12,
      color: '#2563eb',
      backgroundColor: 'rgba(37, 99, 235, 0.08)',
      border: '1px solid rgba(37, 99, 235, 0.12)',
      '&:hover': {
        backgroundColor: 'rgba(37, 99, 235, 0.14)',
      },
    },
    dropzoneArea: {
      height: '100%',
      minHeight: 240,
      border: 'none',
      background: 'transparent',
    },
    uploadHelperRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.25),
    },
    uploadHelperChip: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.55, 1.1),
      borderRadius: 999,
      border: '1px solid rgba(99, 102, 241, 0.14)',
      backgroundColor: 'rgba(255,255,255,0.86)',
      color: '#475569',
      fontSize: '0.76rem',
      fontWeight: 600,
      boxShadow: '0 6px 14px rgba(148, 163, 184, 0.08)',
    },
    uploadHelperText: {
      marginTop: theme.spacing(1.1),
      fontSize: '0.78rem',
      lineHeight: 1.6,
      color: '#64748b',
    },
    uploadGridItem: {
      display: 'flex',
    },
    uploadBody: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(0, 0, 2),
      // height: 320,
    },
    listBody: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(0, 2, 2),
      height: 380,
    },
    fileListScrollArea: {
      height: 380,
      overflowY: 'auto',
      paddingRight: theme.spacing(0.5),
      '&::-webkit-scrollbar': {
        width: 8,
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 999,
        backgroundColor: 'rgba(148, 163, 184, 0.35)',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(241, 245, 249, 0.75)',
        borderRadius: 999,
      },
    },
    fileListEmpty: {
      padding: theme.spacing(3, 2),
      borderRadius: 16,
      border: '1px dashed rgba(148, 163, 184, 0.2)',
      color: '#64748b',
      backgroundColor: 'rgba(248,250,252,0.9)',
      textAlign: 'center',
    },
    attachmentInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      minWidth: 0,
      flex: 1,
    },
    fileIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      display: 'grid',
      placeItems: 'center',
      color: '#4f46e5',
      backgroundColor: 'rgba(79, 70, 229, 0.08)',
      flexShrink: 0,
    },
    fileMeta: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    fileHint: {
      fontSize: '0.76rem',
      color: '#64748b',
      marginTop: theme.spacing(0.25),
    },
  }),
);

const TaskAttachment: React.FC<TaskAttachmentProps> = (props) => {
  const { smTaskId } = props;
  const classes = useStyles();
  const { items: attachmentList } = useSelector<AppState, TaskAttachmentState>(
    (state) => state.taskAttachment,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchTaskAttachmentListByTaskId(smTaskId));
    return function cleanUp() {
      dispatch(clearTaskAttachment());
    };
  }, [dispatch, smTaskId]);

  return (
    <Paper elevation={2} style={{ padding: 12, borderRadius: 16 }}>
      <CardContent style={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} className={classes.uploadGridItem}>
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionIcon}>
                  <CloudUploadOutlined />
                </div>
                <div className={classes.sectionHeaderText}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Upload Files
                  </Typography>
                  <Typography className={classes.sectionHint}>
                    Drag and drop documents here or browse from your device.
                  </Typography>
                </div>
              </div>

              <div className={classes.uploadBody}>
                <div className={classes.container}>
                  <DropzoneArea
                    dropzoneClass={classes.dropzoneArea}
                    key={attachmentList.length}
                    acceptedFiles={['']}
                    filesLimit={20}
                    dropzoneText="Upload File"
                    showPreviewsInDropzone={false}
                    maxFileSize={fileSizeInBytes}
                    onChange={(uploadFiles) => {
                      dispatch(uploadTaskAttachments({ smTaskId, files: uploadFiles }));
                    }}
                    showAlerts={true}
                  />
                  <div className={classes.uploadHelperRow}>
                    <span className={classes.uploadHelperChip}>Up to 20 files</span>
                    <span className={classes.uploadHelperChip}>50 MB per file</span>
                  </div>
                  <Typography className={classes.uploadHelperText}>
                    Supported documents can be dragged into the area above or selected
                    manually from your device.
                  </Typography>
                </div>
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} className={classes.uploadGridItem}>
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <div className={classes.sectionIcon}>
                  <InsertDriveFileOutlined />
                </div>
                <div className={classes.sectionHeaderText}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    File List
                  </Typography>
                  <Typography className={classes.sectionHint}>
                    Download previously uploaded files for this task.
                  </Typography>
                </div>
              </div>

              <div className={classes.listBody}>
                {attachmentList.length > 0 ? (
                  <>
                    <Typography className={classes.title} variant="h6">
                      {attachmentList.length} file{attachmentList.length > 1 ? 's' : ''}
                    </Typography>
                    <div className={classes.fileListScrollArea}>
                      {attachmentList.map((attachment) => {
                        const downloadUrl = `${baseUrl}/${attachment.id}/download`;
                        return (
                          <div
                            key={attachment.id}
                            className={classes.attachmentContainer}
                          >
                            <div className={classes.attachmentInfo}>
                              <div className={classes.fileIcon}>
                                <InsertDriveFileOutlined fontSize="small" />
                              </div>
                              <div className={classes.fileMeta}>
                                <Typography className={classes.attachmentTypo}>
                                  {attachment.fileName}
                                </Typography>
                                <Typography className={classes.fileHint}>
                                  Ready to download
                                </Typography>
                              </div>
                            </div>

                            <Tooltip title="Download" placement="right">
                              <IconButton
                                className={classes.iconButton}
                                onClick={async () => {
                                  const response = await axios({
                                    method: 'GET',
                                    url: `${downloadUrl}`,
                                    responseType: 'blob',
                                  });
                                  saveAs(response.data, attachment.fileName);
                                }}
                              >
                                <CloudDownload />
                              </IconButton>
                            </Tooltip>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className={classes.fileListEmpty}>
                    No attachments uploaded yet.
                  </div>
                )}
              </div>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Paper>
  );
};

export default TaskAttachment;
